import type { ItemId } from '../inventory';
import type { ItemDrop } from '../sector';

interface LootRoll {
  readonly itemId: ItemId;
  readonly weight: number;
  readonly yieldMin: number;
  readonly yieldMax: number;
}

/** Loot table for destroyed miner NPC ships. Most kills give common scrap;
 *  rare uranium / alien-alloy pulls keep an incentive to intercept them
 *  without making it the dominant economic loop. Weights tuned so:
 *
 *    common (iron-scrap + raw-quartz): ~76%
 *    uncommon (silver, ion-ice):       ~18%
 *    rare (uranium, plasma-crystal):    ~5%
 *    epic (alien-alloy):                ~1%
 *
 *  Yields scaled up so a miner kill feels like an actual haul rather
 *  than a couple of scraps — destroying a miner is a real risk, the
 *  payout should match. */
const MINER_LOOT_TABLE: readonly LootRoll[] = [
  { itemId: 'iron-scrap', weight: 38, yieldMin: 3, yieldMax: 7 },
  { itemId: 'raw-quartz', weight: 38, yieldMin: 2, yieldMax: 5 },
  { itemId: 'silver', weight: 9, yieldMin: 2, yieldMax: 4 },
  { itemId: 'ion-ice', weight: 9, yieldMin: 2, yieldMax: 4 },
  { itemId: 'uranium-ore', weight: 3, yieldMin: 1, yieldMax: 3 },
  { itemId: 'plasma-crystal', weight: 2, yieldMin: 1, yieldMax: 2 },
  { itemId: 'alien-alloy', weight: 1, yieldMin: 1, yieldMax: 2 }
];

/** Roll a miner-ship drop. Returns 2-4 stacks: two guaranteed pulls,
 *  with chained bonus rolls so the haul has variety. The combined
 *  expected value matches "real cargo from a hauler" rather than a
 *  scrap field. */
export function rollMinerShipLoot(rng: () => number = Math.random): readonly ItemDrop[] {
  const drops: ItemDrop[] = [pickFromTable(rng), pickFromTable(rng)];
  // 60% chance of a third stack — multi-drops are now the norm,
  // matching a hauler's cargo silhouette.
  if (rng() < 0.6) drops.push(pickFromTable(rng));
  // 25% chance of a fourth stack on top of that — occasional jackpot.
  if (rng() < 0.25) drops.push(pickFromTable(rng));
  return drops;
}

function pickFromTable(rng: () => number): ItemDrop {
  let total = 0;
  for (const entry of MINER_LOOT_TABLE) total += entry.weight;
  let roll = rng() * total;
  let chosen: LootRoll = MINER_LOOT_TABLE[0]!;
  for (const entry of MINER_LOOT_TABLE) {
    roll -= entry.weight;
    if (roll <= 0) {
      chosen = entry;
      break;
    }
  }
  const range = chosen.yieldMax - chosen.yieldMin;
  const count = chosen.yieldMin + Math.floor(rng() * (range + 1));
  return { itemId: chosen.itemId, count };
}
